// Sidebar nav palette — "Ink & Glow, zoned".
//
// Colour answers ONE question here: "which area of the app is this?". The nav is cut into five
// zones and every item inherits its zone's hue, so the 14 items read as 5 chunks instead of 14
// unrelated colours. Tools are deliberately slate so they recede, and the five near-identical
// calendar glyphs share one amber family on purpose — the zone chunks them, the glyph
// disambiguates inside the zone.
//
// At rest the colour lives on the GLYPH, never on a tile — no tile wall down the rail. The
// SELECTED item earns the tile: a translucent zone-500 fill, a hairline inset ring, and (rail
// only) a soft same-hue glow.
//
// Why a ring instead of a border: this app's global stylesheet has an *unlayered*
// `* { @apply border-border }` rule, and in Tailwind v4 unlayered declarations beat layered
// utilities — so a `border-<hue>-500` is silently overridden to the grey --border colour. A ring
// is box-shadow based, so it is immune to that override. `ring-inset` keeps the hairline on the
// tile itself rather than growing the 36px box.
//
// IMPORTANT: every class is a complete literal string. Tailwind v4's content scanner only detects
// whole class names in source, so a constructed name like `bg-${hue}-500/13` would emit no CSS.
// Keep these spelled out per zone (same constraint as src/lib/messages/avatar-color.js).
//
// Opacity recipe, applied identically to every zone (dark values are the mocked ones; light
// follows the same recipe one step darker so the glyphs hold up on a white page):
//   glyph rest    dark zone-300 @ 80% (rail 85%)   light zone-600
//   glyph active  dark zone-300         light zone-700
//   active fill   dark zone-500 @ 13% (rail 18%)   light @ 10%
//   active ring   dark zone-500 @ 35% (rail 40%)   light @ 30%
//   rail glow     dark zone-500 @ 28%              light @ 20%
//   hover fill    zone-500 @ 7%, rest rows only
export const SIDEBAR_ZONE_COLORS = {
  violet: {
    dot: "bg-violet-500",
    label: "text-violet-700/70 dark:text-violet-300/75",
    glyph: "text-violet-600 dark:text-violet-300/80",
    glyphRail: "text-violet-600 dark:text-violet-300/85",
    glyphActive: "text-violet-700 dark:text-violet-300",
    hover: "hover:bg-violet-500/7",
    rowActive: "bg-violet-500/10 ring-1 ring-inset ring-violet-500/30 dark:bg-violet-500/13 dark:ring-violet-500/35",
    tileActive:
      "bg-violet-500/10 shadow-[0_0_14px] shadow-violet-500/20 ring-1 ring-inset ring-violet-500/30 dark:bg-violet-500/18 dark:shadow-violet-500/28 dark:ring-violet-500/40",
  },
  sky: {
    dot: "bg-sky-500",
    label: "text-sky-700/70 dark:text-sky-300/75",
    glyph: "text-sky-600 dark:text-sky-300/80",
    glyphRail: "text-sky-600 dark:text-sky-300/85",
    glyphActive: "text-sky-700 dark:text-sky-300",
    hover: "hover:bg-sky-500/7",
    rowActive: "bg-sky-500/10 ring-1 ring-inset ring-sky-500/30 dark:bg-sky-500/13 dark:ring-sky-500/35",
    tileActive:
      "bg-sky-500/10 shadow-[0_0_14px] shadow-sky-500/20 ring-1 ring-inset ring-sky-500/30 dark:bg-sky-500/18 dark:shadow-sky-500/28 dark:ring-sky-500/40",
  },
  emerald: {
    dot: "bg-emerald-500",
    label: "text-emerald-700/70 dark:text-emerald-300/75",
    glyph: "text-emerald-600 dark:text-emerald-300/80",
    glyphRail: "text-emerald-600 dark:text-emerald-300/85",
    glyphActive: "text-emerald-700 dark:text-emerald-300",
    hover: "hover:bg-emerald-500/7",
    rowActive: "bg-emerald-500/10 ring-1 ring-inset ring-emerald-500/30 dark:bg-emerald-500/13 dark:ring-emerald-500/35",
    tileActive:
      "bg-emerald-500/10 shadow-[0_0_14px] shadow-emerald-500/20 ring-1 ring-inset ring-emerald-500/30 dark:bg-emerald-500/18 dark:shadow-emerald-500/28 dark:ring-emerald-500/40",
  },
  amber: {
    dot: "bg-amber-500",
    label: "text-amber-700/70 dark:text-amber-300/75",
    glyph: "text-amber-600 dark:text-amber-300/80",
    glyphRail: "text-amber-600 dark:text-amber-300/85",
    glyphActive: "text-amber-700 dark:text-amber-300",
    hover: "hover:bg-amber-500/7",
    rowActive: "bg-amber-500/10 ring-1 ring-inset ring-amber-500/30 dark:bg-amber-500/13 dark:ring-amber-500/35",
    tileActive:
      "bg-amber-500/10 shadow-[0_0_14px] shadow-amber-500/20 ring-1 ring-inset ring-amber-500/30 dark:bg-amber-500/18 dark:shadow-amber-500/28 dark:ring-amber-500/40",
  },
  slate: {
    dot: "bg-slate-500",
    label: "text-slate-700/70 dark:text-slate-300/75",
    glyph: "text-slate-600 dark:text-slate-300/80",
    glyphRail: "text-slate-600 dark:text-slate-300/85",
    glyphActive: "text-slate-700 dark:text-slate-300",
    hover: "hover:bg-slate-500/7",
    rowActive: "bg-slate-500/10 ring-1 ring-inset ring-slate-500/30 dark:bg-slate-500/13 dark:ring-slate-500/35",
    tileActive:
      "bg-slate-500/10 shadow-[0_0_14px] shadow-slate-500/20 ring-1 ring-inset ring-slate-500/30 dark:bg-slate-500/18 dark:shadow-slate-500/28 dark:ring-slate-500/40",
  },
};

// Resolve a zone's class set. Slate is the fallback so an unknown/typo'd zone still renders a
// sane recessive row instead of crashing the whole sidebar.
export function getSidebarZoneColors(zone) {
  return SIDEBAR_ZONE_COLORS[zone] || SIDEBAR_ZONE_COLORS.slate;
}
